import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

const TOKEN_KEY = "admin_token";

/**
 * Listens for Shift + A + D pressed simultaneously.
 * - If valid token → navigate to /panel.
 * - Else → opens the AdminLoginModal.
 */
export default function AdminKeybind({ onOpenModal }) {
  const navigate = useNavigate();
  const pressedRef = useRef(new Set());

  useEffect(() => {
    const onKeyDown = (e) => {
      // Track keys currently down
      const k = e.key.toLowerCase();
      pressedRef.current.add(k);
      const set = pressedRef.current;
      // Need: shift + a + d  (shift is also reflected in e.shiftKey)
      if (e.shiftKey && set.has("a") && set.has("d")) {
        e.preventDefault();
        // Drain set so a single combo only triggers once
        pressedRef.current.clear();
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) {
          // verify quickly
          api
            .get("/admin/verify", { headers: { Authorization: `Bearer ${token}` } })
            .then(() => navigate("/panel"))
            .catch(() => {
              localStorage.removeItem(TOKEN_KEY);
              onOpenModal();
            });
        } else {
          onOpenModal();
        }
      }
    };

    const onKeyUp = (e) => {
      pressedRef.current.delete(e.key.toLowerCase());
    };

    const onBlur = () => pressedRef.current.clear();

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    window.addEventListener("blur", onBlur);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
      window.removeEventListener("blur", onBlur);
    };
  }, [navigate, onOpenModal]);

  return null;
}

export function AdminLoginModal({ open, onClose }) {
  const navigate = useNavigate();
  const [usuario, setUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setErr("");
    setUsuario("");
    setPassword("");
    setTimeout(() => inputRef.current?.focus(), 50);
    const onEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErr("");
    try {
      const r = await api.post("/admin/login", { usuario, password });
      localStorage.setItem(TOKEN_KEY, r.data.token);
      onClose();
      navigate("/panel");
    } catch {
      setErr("Credenciales inválidas");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-modal-backdrop" onClick={onClose} data-testid="admin-modal-backdrop">
      <form
        className="admin-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        data-testid="admin-modal"
      >
        <div className="admin-modal-eyebrow">Acceso privado</div>
        <h3>Iniciar sesión</h3>
        <input
          ref={inputRef}
          type="text"
          placeholder="Usuario"
          value={usuario}
          onChange={(e) => setUsuario(e.target.value)}
          data-testid="admin-modal-usuario"
        />
        <input
          type="password"
          placeholder="Contraseña"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-modal-password"
        />
        <button
          type="submit"
          className="btn btn-primary"
          disabled={loading}
          data-testid="admin-modal-submit"
        >
          {loading ? "Un momento…" : "Entrar"}
        </button>
        {err && <div className="admin-error" data-testid="admin-modal-error">{err}</div>}
        <div className="admin-modal-hint">Escape para cerrar</div>
      </form>
    </div>
  );
}
